'use client';

import { useAccountViewAsTarget } from '@/@subsidiary/hooks/useAccountViewAsTarget';
import { getDeployEnvironment } from '@/lib/config/deploy-environment';
import { ENV } from '@/lib/config/environment';
import { EVENTS, tracking } from '@/lib/features/tracking';
import { buildIdentifyTraits } from '@/lib/features/tracking/identify-traits';
import { useSession } from '@/providers/enhanced-session';
import LogRocket from 'logrocket';
import { useEffect, useRef } from 'react';

/**
 * Attaches org and environment identity to the LogRocket session (M4S-5593).
 *
 * This lives in its own component rather than in `PrivateLayout` for one
 * concrete reason: view-as state lives in `SubsidiaryFilterProvider`, which
 * `PrivateLayout` *renders*. An effect in the layout sits above that provider
 * and `useSubsidiaryFilterOptional()` there is always null. Rendered as a child,
 * it can see the masquerade target — and because the subsidiary hooks are
 * null-safe outside their provider, the freemium branch can render the very
 * same component.
 *
 * Renders nothing.
 */
export function LogRocketIdentify() {
    const { data: session } = useSession();
    const viewAsTarget = useAccountViewAsTarget();

    // The session is refetched every 30 minutes and on every window focus, and
    // `useAccountViewAsTarget` changes as the drawer is used. Without this, each
    // refetch re-sends identical traits and fires a duplicate `user_identified`.
    const lastSentRef = useRef<string | null>(null);

    const user = session?.user;
    const org = session?.org;

    useEffect(() => {
        if (!user?.id) return;

        try {
            const traits = buildIdentifyTraits({
                user,
                org,
                subsidiary: session?.subsidiary,
                viewAsTarget,
                environment: getDeployEnvironment().environment,
                buildId: ENV.buildId,
            });

            const fingerprint = `${user.id}|${JSON.stringify(traits)}`;
            if (lastSentRef.current === fingerprint) return;
            lastSentRef.current = fingerprint;

            LogRocket.identify(user.id, traits);

            tracking.event(EVENTS.USER.IDENTIFIED, {
                userId: user.id,
                environment: traits.environment,
            });
        } catch (error) {
            // Never let identification break the authenticated tree.
            tracking.errorWithSource(error as Error, 'LogRocketIdentify', { userId: user.id });
        }
    }, [user, org, session?.subsidiary, viewAsTarget]);

    return null;
}

LogRocketIdentify.displayName = 'LogRocketIdentify';
